/**
 * Adults only — move money between one kid's buckets (e.g. Spend → Save). The Worker
 * writes a matched pair of ledger rows and refuses to let a bucket go negative; this
 * form mirrors that limit so the common case fails fast without a round-trip, but the
 * Worker is the real guard. A failed move keeps the amount on screen.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { api, ApiRequestError, money, parseMoney } from '../../lib/api';
import type { KidWallet, MemberSummary, MoneyBucket } from '../../shared/types';
import {
  BUCKETS,
  Modal,
  bucketMeta,
  btnPrimary,
  btnSecondary,
  cx,
  fieldInput,
  fieldLabel,
} from './shared';

export default function MoveForm({
  member,
  wallet,
  onClose,
  onSaved,
}: {
  member: MemberSummary;
  wallet: KidWallet;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [from, setFrom] = useState<MoneyBucket>('spend');
  const [to, setTo] = useState<MoneyBucket>('save');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fromBalance = wallet[from];
  const toOptions = useMemo(() => BUCKETS.filter((b) => b.key !== from), [from]);

  function pickFrom(next: MoneyBucket) {
    setFrom(next);
    if (to === next) setTo(BUCKETS.find((b) => b.key !== next)!.key);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (from === to) {
      setError('Pick two different buckets.');
      return;
    }
    const cents = parseMoney(amount);
    if (cents === null || cents <= 0) {
      setError('Enter an amount, like 5.00.');
      return;
    }
    if (cents > fromBalance) {
      setError(`${member.name}'s ${bucketMeta(from).label} bucket only has ${money(fromBalance)}.`);
      return;
    }

    setSaving(true);
    try {
      await api.post('/kidmoney/move', { member_id: member.id, from, to, amount: cents });
      await onSaved();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : 'That did not save. Try again in a second.'
      );
      setSaving(false);
    }
  }

  return (
    <Modal title={`Move ${member.name}'s money`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="move-from" className={fieldLabel}>
            From
          </label>
          <select
            id="move-from"
            value={from}
            onChange={(ev) => pickFrom(ev.target.value as MoneyBucket)}
            className={fieldInput}
          >
            {BUCKETS.map((b) => (
              <option key={b.key} value={b.key}>
                {b.emoji} {b.label} — {money(wallet[b.key])}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="move-to" className={fieldLabel}>
            To
          </label>
          <select
            id="move-to"
            value={to}
            onChange={(ev) => setTo(ev.target.value as MoneyBucket)}
            className={fieldInput}
          >
            {toOptions.map((b) => (
              <option key={b.key} value={b.key}>
                {b.emoji} {b.label} — {money(wallet[b.key])}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="move-amount" className={fieldLabel}>
            How much
          </label>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint"
            >
              $
            </span>
            <input
              id="move-amount"
              value={amount}
              onChange={(ev) => setAmount(ev.target.value)}
              className={cx(fieldInput, 'pl-7')}
              inputMode="decimal"
              placeholder="5.00"
              autoFocus
              required
            />
          </div>
          <button
            type="button"
            onClick={() => setAmount((fromBalance / 100).toFixed(2))}
            disabled={fromBalance <= 0}
            className="mt-2 text-sm font-medium text-sage underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-50"
          >
            Move all {money(fromBalance)}
          </button>
        </div>

        {error ? (
          <p role="alert" className="rounded-xl bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={saving} className={cx(btnPrimary, 'flex-1')}>
            {saving ? 'Moving…' : 'Move it'}
          </button>
          <button type="button" onClick={onClose} className={btnSecondary}>
            Cancel
          </button>
        </div>
        <p className="text-xs text-ink-faint">
          Moving money between buckets doesn’t change {member.name}’s total — it just changes what it’s for.
        </p>
      </form>
    </Modal>
  );
}
